import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatListCard, StatListModal } from '@bliks-io/ui';
import type { ShowSystemResponse } from '@bliks-io/contracts';

/** Maximum number of items to display in stat list cards before showing "View All" */
const STAT_LIST_DISPLAY_LIMIT = 5;

interface SystemDetailSidebarProps {
  statistics: NonNullable<ShowSystemResponse['data']['statistics']>;
}

export function SystemDetailSidebar({ statistics }: SystemDetailSidebarProps) {
  const { t } = useTranslation();

  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [departmentsModalOpen, setDepartmentsModalOpen] = useState(false);
  const [orgFunctionsModalOpen, setOrgFunctionsModalOpen] = useState(false);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 2xl:grid-cols-1 gap-6">
        <StatListCard
          title={t('organization.systems.topRoles.title')}
          items={(statistics.topRoles ?? []).map((role) => ({
            id: role._id,
            label: role.name,
            value: role.userCount
          }))}
          emptyMessage={t('organization.systems.topRoles.noRoles')}
          action={
            statistics.allRoles && statistics.allRoles.length > STAT_LIST_DISPLAY_LIMIT
              ? { label: t('common.viewAll'), onClick: () => setRolesModalOpen(true) }
              : undefined
          }
        />
        <StatListCard
          title={t('organization.systems.departments.title')}
          items={(statistics.topDepartments ?? []).map((dept) => ({
            id: dept._id,
            label: dept.name,
            value: dept.userCount,
            suffix: t('organization.systems.departments.membersSuffix')
          }))}
          emptyMessage={t('organization.systems.departments.noDepartments')}
          action={
            statistics.allDepartments && statistics.allDepartments.length > STAT_LIST_DISPLAY_LIMIT
              ? { label: t('common.viewAll'), onClick: () => setDepartmentsModalOpen(true) }
              : undefined
          }
        />
        <StatListCard
          title={t('organization.systems.topOrgFunctions.title')}
          items={(statistics.topOrgFunctions ?? []).map((orgFunction) => ({
            id: orgFunction._id,
            label: orgFunction.name,
            value: orgFunction.userCount,
            suffix: `(${orgFunction.percentage}%)`
          }))}
          emptyMessage={t('organization.systems.topOrgFunctions.noOrgFunctions')}
          action={
            statistics.allOrgFunctions &&
            statistics.allOrgFunctions.length > STAT_LIST_DISPLAY_LIMIT
              ? { label: t('common.viewAll'), onClick: () => setOrgFunctionsModalOpen(true) }
              : undefined
          }
        />
      </div>

      <StatListModal
        open={rolesModalOpen}
        onOpenChange={setRolesModalOpen}
        title={t('organization.systems.topRoles.allRolesTitle')}
        items={(statistics.allRoles ?? []).map((role) => ({
          id: role._id,
          label: role.name,
          value: role.userCount
        }))}
        emptyMessage={t('common.noItemsFound')}
      />
      <StatListModal
        open={departmentsModalOpen}
        onOpenChange={setDepartmentsModalOpen}
        title={t('organization.systems.departments.allDepartmentsTitle')}
        items={(statistics.allDepartments ?? []).map((dept) => ({
          id: dept._id,
          label: dept.name,
          value: dept.userCount,
          suffix: t('organization.systems.departments.membersSuffix')
        }))}
        emptyMessage={t('common.noItemsFound')}
      />
      <StatListModal
        open={orgFunctionsModalOpen}
        onOpenChange={setOrgFunctionsModalOpen}
        title={t('organization.systems.topOrgFunctions.allOrgFunctionsTitle')}
        items={(statistics.allOrgFunctions ?? []).map((orgFunction) => ({
          id: orgFunction._id,
          label: orgFunction.name,
          value: orgFunction.userCount,
          suffix: `(${orgFunction.percentage}%)`
        }))}
        emptyMessage={t('common.noItemsFound')}
      />
    </>
  );
}
